import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { CloseIcon, CloudUploadIcon } from "../../../../Icons";
import {
  getPatientClinicalForms,
  uploadClinicalFormAttachment,
  uploadDailyLogAttachment,
  fileToBase64,
} from "../../../../services/patientService";
import { listDailyLogs } from "../../../../services/dailyLogService";
import { PATIENT_CLINICAL_FORM_SECTIONS } from "../../clinicalFormCatalog";
import { EVAL_DAILY_LOG_SECTION_KEY } from "./dailyLogEval";
import { displayDateUS } from "../../../../utils/dateUtils";
import {
  clinicalFormScheduleDate,
  sortClinicalFormsByScheduleDateAsc,
} from "./sortClinicalForms";
import {
  MODAL_BACKDROP,
  MODAL_PANEL,
  MODAL_HEADER,
  MODAL_TITLE,
  MODAL_BODY,
  MODAL_FOOTER,
  BTN_PRIMARY,
  BTN_SECONDARY,
  SPINNER,
} from "../../../../ui/pageChrome";
import {
  CLINICAL_ATTACHMENT_UPLOAD_ACCEPT,
  CLINICAL_ATTACHMENT_UPLOAD_TYPES_LABEL,
  validatePatientUploadFileClient,
} from "../../patientUploadTypes";

const SECTION_LABELS = {
  "admission-documents": "Admission Documents",
  "physician-orders": "Physician Orders",
  "plan-of-care": "Plan of Care",
  "soc-roc-recert": "SOC/ROC/RECERT/TRANSFER",
  "nurse-notes": "Nurse Notes",
  "pt-notes": "PT Notes",
  "ot-notes": "OT Notes",
  "st-notes": "ST Notes",
  "msw-notes": "MSW Notes",
  "chha-notes": "CHHA Notes",
  [EVAL_DAILY_LOG_SECTION_KEY]: "Daily Logs",
};

const ACCEPT =
  CLINICAL_ATTACHMENT_UPLOAD_ACCEPT;

/**
 * Upload Documents popup — pick section + form, then attach one or more files.
 */
const ClinicalFormUploadModal = ({
  open,
  patientId,
  patient,
  defaultSectionKey,
  defaultFormId = null,
  onClose,
  onUploaded,
}) => {
  const [sectionKey, setSectionKey] = useState(defaultSectionKey || "");
  const [forms, setForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [formId, setFormId] = useState(defaultFormId);
  const [displayName, setDisplayName] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const isDailyLogSection = sectionKey === EVAL_DAILY_LOG_SECTION_KEY;

  const sections = useMemo(
    () =>
      [
        ...(PATIENT_CLINICAL_FORM_SECTIONS || Object.keys(SECTION_LABELS)),
        EVAL_DAILY_LOG_SECTION_KEY,
      ]
        .filter((key, idx, arr) => arr.indexOf(key) === idx)
        .map((key) => ({
          key,
          label: SECTION_LABELS[key] || key,
        })),
    [],
  );

  useEffect(() => {
    if (!open) return;
    setSectionKey(defaultSectionKey || sections[0]?.key || "");
    setFormId(defaultFormId);
    setDisplayName("");
    setFiles([]);
  }, [open, defaultSectionKey, defaultFormId, sections]);

  useEffect(() => {
    if (!open || !sectionKey || !patientId) return undefined;
    let cancelled = false;
    (async () => {
      setLoadingForms(true);
      try {
        let list = [];
        if (sectionKey === EVAL_DAILY_LOG_SECTION_KEY) {
          const res = await listDailyLogs({
            patientId,
            page: 1,
            limit: 200,
          });
          const rows = res.result?.dailyLogs || res.dailyLogs || [];
          list = sortClinicalFormsByScheduleDateAsc(
            rows.map((log) => ({
            id: log.id,
            formName: log.formName || "Daily Log",
            scheduleDate:
              log.scheduleDate ||
              log.serviceDate ||
              log.scheduledVisit?.visitDate ||
              null,
            })),
          );
        } else {
          const res = await getPatientClinicalForms(patientId, {
            section: sectionKey,
          });
          list = sortClinicalFormsByScheduleDateAsc(
            res.result?.forms || res.forms || [],
          );
        }
        if (cancelled) return;
        setForms(list);
        setFormId((prev) => {
          if (prev && list.some((f) => Number(f.id) === Number(prev))) {
            return prev;
          }
          return list[0]?.id || null;
        });
      } catch (err) {
        if (!cancelled) {
          toast.error(err.message || "Failed to load forms");
          setForms([]);
        }
      } finally {
        if (!cancelled) setLoadingForms(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, sectionKey, patientId]);

  if (!open) return null;

  const patientLabel = patient
    ? [patient.lastName, patient.firstName].filter(Boolean).join(", ") ||
      patient.mrn ||
      "Patient"
    : "Patient";

  const onPickFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    const accepted = [];
    const rejected = [];
    for (const file of list) {
      const check = validatePatientUploadFileClient(file);
      if (!check.ok) {
        rejected.push(`${file.name} (${check.message})`);
        continue;
      }
      accepted.push(file);
    }
    if (rejected.length) {
      toast.error(
        `${rejected.slice(0, 2).join("; ")}${
          rejected.length > 2 ? ` (+${rejected.length - 2} more)` : ""
        }`,
      );
    }
    if (accepted.length) {
      setFiles((prev) => [...prev, ...accepted]);
    }
    e.target.value = "";
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!formId) {
      toast.error("Select a document/form to attach to");
      return;
    }
    if (!files.length) {
      toast.error("Choose at least one file");
      return;
    }
    setSaving(true);
    try {
      let uploaded = 0;
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const fileBase64 = await fileToBase64(file);
        const name =
          files.length === 1 && displayName.trim()
            ? displayName.trim()
            : displayName.trim()
              ? `${displayName.trim()} (${i + 1})`
              : file.name;
        await (isDailyLogSection
          ? uploadDailyLogAttachment(patientId, formId, {
              fileBase64,
              fileName: file.name,
              displayName: name,
            })
          : uploadClinicalFormAttachment(patientId, formId, {
              fileBase64,
              fileName: file.name,
              displayName: name,
            }));
        uploaded += 1;
      }
      toast.success(
        uploaded === 1
          ? "Document uploaded"
          : `${uploaded} documents uploaded`,
      );
      onUploaded?.(formId);
      onClose?.();
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className={MODAL_BACKDROP} onClick={onClose}>
      <div
        className={`${MODAL_PANEL} max-w-lg`}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={MODAL_HEADER}>
          <div className="min-w-0">
            <h3 className={MODAL_TITLE}>Upload Documents</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Selected Patient: {patientLabel}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
            aria-label="Close"
          >
            <CloseIcon size={14} />
          </button>
        </div>

        <div className={MODAL_BODY}>
          <p className="text-[11px] text-slate-500">
            Choose the section and document where these files should be
            attached.
          </p>

          <div>
            <label className="block text-[11px] font-medium text-slate-600 mb-1">
              Section
            </label>
            <select
              value={sectionKey}
              onChange={(e) => setSectionKey(e.target.value)}
              className="w-full h-8 px-2.5 text-sm border border-slate-200 rounded-md outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            >
              {sections.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-medium text-slate-600 mb-1">
              Document / Form
            </label>
            {loadingForms ? (
              <p className="text-xs text-slate-400 animate-pulse">Loading…</p>
            ) : forms.length === 0 ? (
              <p className="text-xs text-slate-500">
                No {isDailyLogSection ? "daily logs" : "forms"} in this section
                yet. Create one first.
              </p>
            ) : (
              <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
                {forms.map((f) => {
                  const active = Number(formId) === Number(f.id);
                  const scheduleLabel =
                    displayDateUS(clinicalFormScheduleDate(f)) || "";
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setFormId(f.id)}
                      className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                        active
                          ? "bg-indigo-600 text-white"
                          : "bg-white text-slate-700 hover:bg-indigo-50"
                      }`}
                    >
                      <span className="flex items-center justify-between gap-3 min-w-0">
                        <span className="truncate font-medium">
                          {f.formName || "Form"}
                        </span>
                        <span
                          className={`shrink-0 tabular-nums ${
                            active ? "text-indigo-100" : "text-slate-500"
                          }`}
                        >
                          {scheduleLabel || "—"}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <label className="block text-[11px] font-medium text-slate-600 mb-1">
              Display Name{" "}
              <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. FACESHEET"
              className="w-full h-8 px-2.5 text-sm border border-slate-200 rounded-md outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            />
          </div>

          <div>
            <label className="block text-[11px] font-medium text-slate-600 mb-1">
              Files
            </label>
            <label className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium text-indigo-700 bg-white border border-indigo-300 rounded-md hover:bg-indigo-50 cursor-pointer">
              <CloudUploadIcon size={14} />
              Browse
              <input
                type="file"
                multiple
                accept={ACCEPT}
                className="hidden"
                onChange={onPickFiles}
              />
            </label>
            {files.length > 0 ? (
              <ul className="mt-2 space-y-1 max-h-28 overflow-y-auto">
                {files.map((f, idx) => (
                  <li
                    key={`${f.name}-${idx}`}
                    className="flex items-center gap-2 text-[11px] text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1"
                  >
                    <span className="truncate flex-1">{f.name}</span>
                    <button
                      type="button"
                      className="text-slate-400 hover:text-red-600"
                      onClick={() => removeFile(idx)}
                      aria-label="Remove file"
                    >
                      <CloseIcon size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <p className="mt-1.5 text-[10px] text-emerald-700 leading-snug">
              Allowed: {CLINICAL_ATTACHMENT_UPLOAD_TYPES_LABEL}
            </p>
          </div>

          <div className={MODAL_FOOTER}>
            <button type="button" className={BTN_SECONDARY} onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className={BTN_PRIMARY}
              disabled={saving || !formId || !files.length}
              onClick={submit}
            >
              {saving ? <span className={SPINNER} /> : null}
              Save
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ClinicalFormUploadModal;
